/**
 * The widget's DOM, element for element with the live v2.1.0 widget, so the verbatim
 * styles.css renders it unchanged. Markup here is static; nothing user-supplied.
 */

import botIcon from "./assets/esl-bot.svg";
import { CHATBOT_NAME } from "./config";

export const ICON = {
  maximize: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"></path></svg>`,
  minimize: `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"></path></svg>`,
};

export interface WidgetElements {
  buttonContainer: HTMLDivElement;
  panelContainer: HTMLDivElement;
  button: HTMLButtonElement;
  panel: HTMLDivElement;
  header: HTMLDivElement;
  messages: HTMLDivElement;
  welcomeScreen: HTMLDivElement;
  thinkingIndicator: HTMLDivElement;
  sessionBar: HTMLDivElement;
  endChatBtn: HTMLButtonElement;
  inputArea: HTMLFormElement;
  input: HTMLInputElement;
  sendBtn: HTMLButtonElement;
  newChatBtn: HTMLButtonElement;
  maximizeBtn: HTMLButtonElement;
  closeBtn: HTMLButtonElement;
  resizeHandle: HTMLDivElement;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  html = "",
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (html) node.innerHTML = html;
  return node;
}

export function botIconImg(className: string): HTMLImageElement {
  const img = el("img", className);
  img.src = botIcon;
  img.alt = CHATBOT_NAME;
  return img;
}

function headerButton(className: string, label: string, tooltip: string, html: string) {
  const button = el("button", `chat-widget-header-icon-button ${className}`, html);
  button.type = "button";
  button.setAttribute("aria-label", label);
  button.setAttribute("data-tooltip", tooltip);
  return button;
}

// The four starting points, one per Phase 1 area. Hints say what the bot will ask for.
const ACTIONS: [action: string, label: string, hint: string, svg: string][] = [
  ["track", "Track a shipment", "With a BL, booking or container number", '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line>'],
  ["eta", "Arrival or departure time", "ETA or ETD for your shipment", '<circle cx="12" cy="12" r="10"></circle><polyline points="12 6 12 12 16 14"></polyline>'],
  ["route", "Sailing schedule", "Sailings and transit time between two ports", '<rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line>'],
  ["vessel", "Vessel schedule", "Arrivals and departures for a vessel or voyage", '<path d="M12 10.189V14"></path><path d="M12 2v3"></path><path d="M19 13V7a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v6"></path><path d="M19.38 20A11.6 11.6 0 0 0 21 14l-8.188-3.639a2 2 0 0 0-1.624 0L3 14a11.6 11.6 0 0 0 2.81 7.76"></path><path d="M2 21c.6.5 1.2 1 2.5 1 2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1s1.2 1 2.5 1c2.5 0 2.5-2 5-2 1.3 0 1.9.5 2.5 1"></path>'],
];

const WELCOME = `
  <h2 class="chat-widget-welcome-title">How can we help with your shipment?</h2>
  <p class="chat-widget-welcome-lede">Ask in your own words, or start here.</p>
  <div class="chat-widget-action-buttons">${ACTIONS.map(
    ([action, label, hint, svg]) => `
    <button type="button" class="chat-widget-action-button" data-action="${action}">
      <span class="chat-widget-action-icon" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${svg}</svg></span>
      <span class="chat-widget-action-label">${label}</span>
      <span class="chat-widget-action-hint">${hint}</span>
    </button>`,
  ).join("")}
  </div>`;

const THINKING = `
  <svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C9.243 2 7 4.243 7 7v3H6c-1.103 0-2 .897-2 2v7c0 1.103.897 2 2 2h12c1.103 0 2-.897 2-2v-7c0-1.103-.897-2-2-2h-1V7c0-2.757-2.243-5-5-5zm0 2c1.654 0 3 1.346 3 3v3h-6V7c0-1.654 1.346-3 3-3zm-4 8h8v5H8v-5z"/>
    <path d="M11 16h2v2h-2zm-4-3h2v2H7zm8 0h2v2h-2z"/>
  </svg>
  <span class="thinking-text">${CHATBOT_NAME} is analyzing...</span>
  <span class="dot"></span><span class="dot"></span><span class="dot"></span>`;

const FOOTER =
  'This chatbot is powered by <a href="https://www.exaqube.com" target="_blank" rel="noopener noreferrer">Exaqube</a> AI, designed to assist you with our services. For more information, check out the <a href="https://www.emiratesline.com/data-privacy/" target="_blank" rel="noopener noreferrer">Privacy Policy</a> or usage Terms.';

export function createWidgetDom(): WidgetElements {
  const buttonContainer = el("div", "chat-widget-button-container");
  buttonContainer.setAttribute("aria-live", "polite");
  const panelContainer = el("div", "chat-widget-panel-container");
  panelContainer.style.pointerEvents = "none";

  const button = el(
    "button",
    "chat-widget-button",
    `<svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/><path d="M7 9h10M7 13h7" stroke="white" stroke-width="2" stroke-linecap="round"/></svg>`,
  );
  button.type = "button";
  button.setAttribute("aria-label", "Toggle Chat Window");
  button.setAttribute("aria-expanded", "false");

  const panelId = `chat-widget-panel-${crypto.randomUUID().slice(0, 8)}`;
  const panel = el("div", "chat-widget-panel");
  panel.id = panelId;
  panel.setAttribute("role", "log");
  panel.setAttribute("aria-hidden", "true");
  panel.setAttribute("aria-labelledby", `${panelId}-label`);
  button.setAttribute("aria-controls", panelId);

  const header = el("div", "chat-widget-header");
  const title = el("div", "chat-widget-title");
  title.id = `${panelId}-label`;
  const titleText = el("span", "");
  titleText.textContent = CHATBOT_NAME;
  title.append(botIconImg("chat-widget-title-icon"), titleText);

  const newChatBtn = headerButton(
    "chat-widget-new-chat-icon",
    "Start a new conversation",
    "New conversation",
    `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>`,
  );
  const maximizeBtn = headerButton(
    "chat-widget-maximize-icon",
    "Maximize Chat Window",
    "Maximize",
    ICON.maximize,
  );
  const closeBtn = headerButton("chat-widget-close", "Close Chat Window", "Close", "×");
  const headerButtons = el("div", "chat-widget-header-buttons");
  headerButtons.append(newChatBtn, maximizeBtn, closeBtn);
  header.append(title, headerButtons);

  const messages = el("div", "chat-widget-messages");
  messages.setAttribute("aria-relevant", "additions text");
  const welcomeScreen = el("div", "chat-widget-welcome", WELCOME);
  const thinkingIndicator = el("div", "thinking-indicator", THINKING);
  thinkingIndicator.setAttribute("aria-label", `${CHATBOT_NAME} is typing`);
  thinkingIndicator.setAttribute("role", "status");
  messages.append(welcomeScreen, thinkingIndicator);

  // Shown once a conversation has started: ends it, with the satisfaction survey.
  const sessionBar = el("div", "chat-widget-session-bar");
  sessionBar.hidden = true;
  const endChatBtn = el("button", "chat-widget-end-chat");
  endChatBtn.type = "button";
  endChatBtn.textContent = "End chat";
  sessionBar.append(endChatBtn);

  const inputArea = el("form", "chat-widget-input");
  const input = el("input", "");
  input.type = "text";
  input.placeholder = "Ask about a shipment…";
  input.autocomplete = "off";
  input.maxLength = 1000;
  input.setAttribute("aria-label", "Chat message input");
  input.setAttribute("aria-required", "true");
  const sendBtn = el(
    "button",
    "",
    `<svg aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>`,
  );
  sendBtn.type = "submit";
  sendBtn.setAttribute("aria-label", "Send message");
  inputArea.append(input, sendBtn);

  const footer = el("div", "chat-widget-footer", FOOTER);
  const resizeHandle = el("div", "chat-widget-resize-handle");
  resizeHandle.setAttribute("aria-hidden", "true");

  panel.append(header, messages, sessionBar, inputArea, footer, resizeHandle);
  buttonContainer.append(button);
  panelContainer.append(panel);
  document.body.append(buttonContainer, panelContainer);

  return {
    buttonContainer,
    panelContainer,
    button,
    panel,
    header,
    messages,
    welcomeScreen,
    thinkingIndicator,
    sessionBar,
    endChatBtn,
    inputArea,
    input,
    sendBtn,
    newChatBtn,
    maximizeBtn,
    closeBtn,
    resizeHandle,
  };
}
